import { defineMessages } from 'react-intl';

/** Chrome copy for the voice workspace overlay. Document titles / step labels
 * are server-authored data (like tool `title`), not catalog entries. */
export const voiceWorkspaceMessages = defineMessages({
  overlayAriaLabel: {
    defaultMessage: 'Heidi workspace',
    id: 'B4yuIgMPj8',
    description: 'Accessible name for the full-screen Heidi voice workspace overlay',
  },
  minimize: {
    defaultMessage: 'Minimise',
    id: 'V1J7WrKhUJ',
    description: 'Button that minimises the Heidi workspace to reveal the app underneath',
  },
  restore: {
    defaultMessage: 'Back to workspace',
    id: 'lqGqIBxHXt',
    description: 'Button that restores the minimised Heidi workspace',
  },
  close: {
    defaultMessage: 'Close workspace',
    id: 'O+DymerY/z',
    description: 'Button that closes the Heidi workspace',
  },
  activityTitle: {
    defaultMessage: 'Activity',
    id: 'TEM7pPo4sI',
    description: 'Heading for the activity and progress rail in the Heidi workspace',
  },
  emptyActivity: {
    defaultMessage: "Heidi's progress will appear here.",
    id: 'oRX5B6Ox4l',
    description: 'Empty state for the Heidi workspace activity rail before any steps run',
  },
  draftedByHeidi: {
    defaultMessage: 'Drafted by Heidi',
    id: '5WsztUnyY3',
    description: 'Eyebrow label indicating the workspace document was drafted by Heidi',
  },
  titleWithVersion: {
    defaultMessage: '{title} · v{version, number}',
    id: '1Nbl1njztG',
    description: 'Workspace draft title with its revision number',
  },
  revisionWithVersion: {
    defaultMessage:
      '{revision, select, previous {Previous revision} other {Drafted by Heidi}} · v{version, number}',
    id: '8Rni7vrWC2',
    description: 'Workspace draft eyebrow with its revision state and number',
  },
  create: {
    defaultMessage: 'Create',
    id: 'TpZg0zZLyn',
    description:
      'Primary action that commits the drafted document in the Heidi workspace',
  },
  created: {
    defaultMessage: 'Created',
    id: 'zHfhdDoqOz',
    description: 'State label after the drafted document has been created',
  },
  discard: {
    defaultMessage: 'Discard',
    id: 'wYPTzBSEOR',
    description:
      'Secondary action that discards the drafted document in the Heidi workspace',
  },
  editDraft: {
    defaultMessage: 'Edit draft',
    id: 'mf5fBPD5fv',
    description:
      'Accessible label for the editable draft document content in the Heidi workspace',
  },
  statusDraft: {
    defaultMessage: 'Draft',
    id: 'i76N7Ypb76',
    description: 'Status chip on the workspace document while it is still a draft',
  },
  statusReady: {
    defaultMessage: 'Ready to review',
    id: 'rDZM5G9gcL',
    description:
      'Status chip when the workspace draft is ready for the clinician to review',
  },
  statusSuperseded: {
    defaultMessage: 'Superseded',
    id: 'gNpa9pTQtx',
    description: 'Status chip on a draft revision replaced by a newer one',
  },
  previousRevision: {
    defaultMessage: 'Previous revision',
    id: 'j24AaEf9i/',
    description: 'Eyebrow on a superseded inline draft card',
  },
  previousRevisionHint: {
    defaultMessage: 'Previous revision — Create moved to the latest',
    id: '0vxEnCZiPm',
    description: 'Collapsed subtitle for a superseded inline draft',
  },
  supersededFooter: {
    defaultMessage: 'Superseded — Create lives on the latest revision below.',
    id: 'CZGv/oU0iu',
    description: 'Footer on an expanded superseded inline draft',
  },
  createdFooter: {
    defaultMessage: 'Created — committed to the session.',
    id: 'GNiefcLv56',
    description: 'Footer on an expanded created inline draft',
  },
  emptyTitle: {
    defaultMessage: "Hey I'm Heidi, how can I help?",
    id: 'X/r+j1Bv6c',
    description:
      'Greeting in the idle Voice workspace before the clinician has said anything',
  },
  quickStartSession: {
    defaultMessage: 'Start a session',
    id: 'OjDyfONjq1',
    description: 'Idle Voice quick-start chip that asks Heidi to start a session',
  },
  quickBrainstorm: {
    defaultMessage: 'Brainstorm ideas',
    id: 'oW1GKHbuBr',
    description: 'Idle Voice quick-start chip that asks Heidi to brainstorm',
  },
  quickDictateNotes: {
    defaultMessage: 'Dictate notes',
    id: 'Bw9JBGlXQD',
    description: 'Idle Voice quick-start chip that asks Heidi to take dictated notes',
  },
  quickCaptureWork: {
    defaultMessage: 'Capture work',
    id: 'pfF8Z4cOgU',
    description: 'Idle Voice quick-start chip that asks Heidi to capture work',
  },
  quickRequestStartSession: {
    defaultMessage: 'Start a session.',
    id: 'xSEzhwkmQu',
    description: 'Message sent to the voice agent when Start a session is clicked',
  },
  quickRequestBrainstorm: {
    defaultMessage: 'Brainstorm ideas with me.',
    id: 'KMjBRp7N72',
    description: 'Message sent to the voice agent when Brainstorm ideas is clicked',
  },
  quickRequestDictateNotes: {
    defaultMessage: 'I want to dictate notes.',
    id: 'B2LPBGTPoJ',
    description: 'Message sent to the voice agent when Dictate notes is clicked',
  },
  quickRequestCaptureWork: {
    defaultMessage: 'Help me capture this work.',
    id: 'GZilSRDU9g',
    description: 'Message sent to the voice agent when Capture work is clicked',
  },
  quickReferral: {
    defaultMessage: 'Referral letter',
    id: '8hs00eEvEI',
    description:
      'Quick-start action that drafts a referral letter in the Heidi workspace',
  },
  quickEmail: {
    defaultMessage: 'Patient email',
    id: 'RZyFooZlp3',
    description: 'Quick-start action that drafts a patient email in the Heidi workspace',
  },
  quickNote: {
    defaultMessage: 'Session note',
    id: '3xHdpp2lga',
    description: 'Quick-start action that drafts a session note in the Heidi workspace',
  },
  quickRequestReferral: {
    defaultMessage: 'Draft a referral letter for this session.',
    id: 'otxWsjdqRg',
    description:
      'Message sent to the voice agent when the referral-letter quick-start is clicked',
  },
  quickRequestEmail: {
    defaultMessage: 'Draft a patient email for this session.',
    id: '82cPscuWfX',
    description:
      'Message sent to the voice agent when the patient-email quick-start is clicked',
  },
  quickRequestNote: {
    defaultMessage: 'Draft a session note for this session.',
    id: '+gqzgJHPfM',
    description:
      'Message sent to the voice agent when the session-note quick-start is clicked',
  },
  createFailed: {
    defaultMessage: 'Could not create the document. Your draft is unchanged — try again.',
    id: 'SgfJ56rLFq',
    description:
      'Inline error next to the Create button when committing the workspace draft fails',
  },
  createNoSession: {
    defaultMessage: 'Open a session first so the document has somewhere to go.',
    id: 'vfjR2WRhxm',
    description:
      'Inline error next to the Create button when no session is associated with the draft',
  },
  minimizedTitle: {
    defaultMessage: 'Heidi workspace',
    id: 'jkJlx/4Fc2',
    description: 'Title on the minimised Heidi workspace pill',
  },
  restoreHint: {
    defaultMessage: 'Tap to reopen',
    id: 'DZ7d3SXDPl',
    description: 'Hint on the minimised Heidi workspace pill',
  },
  presentedByHeidi: {
    defaultMessage: 'Presented by Heidi',
    id: 'GVPk9E9ZWW',
    description: 'Eyebrow label on an information card Heidi presented in the workspace',
  },
  evidenceCardAriaLabel: {
    defaultMessage: 'Key Points',
    id: 'FfVDlcO5SO',
    description:
      'Accessible name for an Evidence Key Points card on the workspace info board',
  },
  infoFeedLabel: {
    defaultMessage: 'Information cards',
    id: 's7O5b7YL1S',
    description:
      'Accessible name for the scrollable feed of information cards in the Heidi workspace',
  },
  attentionBadge: {
    defaultMessage: 'Needs attention',
    id: 'M6+p6OFJrn',
    description: 'Badge on an information card that carries a safety-relevant flag',
  },
  goDeeper: {
    defaultMessage: 'Go deeper',
    id: 'w2AUAvotSG',
    description:
      'Label above the drill-down suggestion chips on a workspace information card',
  },
  backToDraft: {
    defaultMessage: 'Back to draft',
    id: 'Q2R0OQBSVY',
    description:
      'Button that returns from the information feed to the draft it displaced',
  },
  quickOverview: {
    defaultMessage: 'Patient overview',
    id: 'd4lIeHrOCh',
    description:
      'Quick-start action that presents a patient overview card in the Heidi workspace',
  },
  quickRequestOverview: {
    defaultMessage: 'Give me an overview of this patient.',
    id: 'DiMqqHI3eQ',
    description:
      'Message sent to the voice agent when the patient-overview quick-start is clicked',
  },
});
